/**
 * Centralized API utility — all requests go through here.
 * Uses Bearer token stored in sessionStorage (tab-scoped, survives top-level
 * redirects within the same tab — needed for OAuth round-trips).
 */
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ventas_auth_token';

// ── Internal: detect PostHog/session-recording postMessage errors ──
// These bubble out of fetch() when a Request object can't be structured-cloned
// by analytics tooling. The underlying fetch usually completed successfully;
// callers can safely ignore them and refresh state.
function isPostMessageNoise(err) {
  if (!err) return false;
  const msg = (err.message || '').toString();
  return (
    msg.includes('postMessage') ||
    msg.includes('Request object could not be cloned') ||
    msg.includes('DataCloneError')
  );
}
export { isPostMessageNoise };

// ── Token store (sessionStorage with in-memory cache) ──
let _accessToken = (typeof window !== 'undefined' && window.sessionStorage)
  ? window.sessionStorage.getItem(TOKEN_KEY)
  : null;

export function setToken(token) {
  _accessToken = token;
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    // sessionStorage may be disabled (private mode, etc.) — non-fatal,
    // in-memory token still works for the lifetime of the tab.
    console.warn('Token persistence unavailable:', err);
  }
}

export function getToken() {
  return _accessToken;
}

export function clearToken() {
  _accessToken = null;
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    console.warn('Token clear failed:', err);
  }
}

// ── Internal headers builder ──
function buildHeaders(extra = {}) {
  const headers = { ...extra };
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return headers;
}

// ── Core fetch wrapper (used by apiGet/apiPost/etc.) ──
async function apiFetch(path, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(`${API}${path}`, {
    headers: buildHeaders({
      'Content-Type': 'application/json',
      ...extraHeaders,
    }),
    ...rest,
  });

  if (res.status === 401 && !options._retried) {
    window.dispatchEvent(new Event('auth:expired'));
    throw new Error('Session expired');
  }

  return res;
}

// ── Drop-in replacement for native fetch (for files not yet migrated to apiGet/apiPost) ──
export async function authFetch(url, options = {}) {
  const { headers: extraHeaders, credentials, ...rest } = options;
  const headers = buildHeaders(extraHeaders || {});
  const res = await fetch(url, { headers, ...rest });

  if (res.status === 401) {
    window.dispatchEvent(new Event('auth:expired'));
  }

  return res;
}

// ── Convenience methods ──
export async function apiGet(path) {
  return apiFetch(path, { method: 'GET' });
}

export async function apiPost(path, body) {
  return apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
}

export async function apiPut(path, body) {
  return apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });
}

export async function apiPatch(path, body) {
  return apiFetch(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function apiDelete(path) {
  return apiFetch(path, { method: 'DELETE' });
}

/** For file uploads (no Content-Type — let browser set boundary) */
export async function apiUpload(path, formData) {
  const headers = {};
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });
}

/**
 * Universal PDF download — stores PDF server-side, returns a direct download URL.
 * Use this for ALL file downloads. No blobs, no createObjectURL.
 *
 * @param {string} apiPath - The API path that returns PDF bytes (e.g., '/api/meetings/123/followup-pdf')
 * @param {string} filename - Desired download filename
 * @param {object} [options] - Optional fetch options (method, body, etc.)
 * @returns {Promise<void>}
 */
export async function downloadFile(apiPath, filename, options = {}) {
  // Step 1: Fetch the file bytes from the authenticated endpoint
  const res = await authFetch(`${API}${apiPath}`, options);
  if (!res.ok) throw new Error('Download failed');

  // Step 2: Read as base64 and store server-side
  const buffer = await res.arrayBuffer();
  const base64 = btoa(
    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );

  const storeRes = await authFetch(`${API}/api/store-download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, data: base64 }),
  });
  if (!storeRes.ok) throw new Error('Failed to prepare download');
  const { download_url, download_id } = await storeRes.json();

  // Step 3: Navigate to the signed download URL (HMAC + 5-min TTL). The
  // signature *is* the authorization — the endpoint requires no JWT header
  // because the browser handles it as a native navigation.
  window.location.href = `${API}${download_url || `/api/download/${download_id}`}`;
}

export default apiFetch;
